/**
 * Shorts branding — the common (per-streamer) settings applied to every short:
 * which platform logo, the channel name, and the CTA label. These live OUTSIDE
 * the EDL (they are shared across a streamer's shorts, not per-project).
 *
 * In Phase A the preview uses {@link DEFAULT_BRANDING}; Phase B feeds the
 * streamer's configured values in via `Compositor.setBranding`.
 */
export type ShortsPlatform = 'youtube' | 'twitch' | 'chzzk' | 'cime';

export interface ShortsBranding {
  platform: ShortsPlatform;
  channelName: string;
  ctaText: string;
}

/** yoy.live common default — used when a streamer has not configured branding. */
export const DEFAULT_BRANDING: ShortsBranding = {
  platform: 'youtube',
  channelName: 'yoy.live',
  ctaText: 'yoy.live',
};

export interface PlatformLogo {
  label: string;
  /** Badge background, #RRGGBB. */
  color: string;
  /** Glyph fill, #RRGGBB. */
  fg: string;
  /** SVG path in a 0..24 viewBox. */
  path: string;
  /**
   * Fill rule for glyphs with punched-out holes (eyes, counters). Omitted → the
   * SVG default (nonzero), which is what every solid mark here wants.
   */
  fillRule?: 'evenodd';
}

/**
 * Platform marks — mirrored from `apps/streamer` `Branding.tsx` PREVIEW_PLATFORMS
 * so the editor preview and the streamer console show the same logos.
 */
export const PLATFORM_LOGOS: Record<ShortsPlatform, PlatformLogo> = {
  youtube: {
    label: 'YouTube',
    color: '#FF0000',
    fg: '#ffffff',
    path: 'M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z',
  },
  twitch: {
    label: 'Twitch',
    color: '#9146FF',
    fg: '#ffffff',
    path: 'M4 2 3 6v13h5v3h3l3-3h4l4-4V2H4zm16 11-3 3h-5l-3 3v-3H6V4h14v9zm-5-7h-2v6h2V6zm-5 0H8v6h2V6z',
  },
  chzzk: {
    label: 'CHZZK',
    color: '#00FFA3',
    fg: '#0b0b0f',
    path: 'M13.8 2 5 13.2h5.2L9 22l9.5-12.2h-5.6L13.8 2z',
  },
  cime: {
    label: '씨미',
    color: '#8956FB',
    fg: '#141517',
    // Same glyph as landing `platformLogos.tsx` CimeLogo (traced from the
    // official symbol PNG). The two eyes are holes — without evenodd this
    // renders as a solid blob.
    path: 'M6.6 3C5.6 3 4.6 4.6 3.9 6.4 3.2 8.4 2.3 11 1.9 12.6 1.6 13.6 1.5 14.3 1.5 15.4 1.5 17.2 1.9 19 2.6 19.9 3.3 20.7 4.1 21 5 21 6 21 6.9 20.7 7.5 20.1 8.3 19.4 9.2 18.9 10.2 18.6L13.8 18.6C14.8 18.9 15.7 19.4 16.5 20.1 17.1 20.7 18 21 19 21 19.9 21 20.7 20.7 21.4 19.9 22.1 19 22.5 17.2 22.5 15.4 22.5 14.3 22.4 13.6 22.1 12.6 21.7 11 20.8 8.4 20.1 6.4 19.4 4.6 18.4 3 17.4 3 16.4 3 15.4 4 14.6 5.2 14.2 5.8 13.9 5.95 13.4 5.95L10.6 5.95C10.1 5.95 9.8 5.8 9.4 5.2 8.6 4 7.6 3 6.6 3ZM7.64 9.56a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2ZM16.36 9.56a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2Z',
    fillRule: 'evenodd',
  },
};
